// apps/client/src/world/terrain/beach.ts — the look demo's vertex-coloured sand slope and sea shelf, over the whole map (DESIGN.md "Beach")
import type { MapData } from '@whiskerton/shared';
import * as THREE from 'three';
import { aboveBeach, beachHeight, SEA_LEVEL } from './field';

const SHELF = new THREE.Color('#7EC6BE'), SEABED = new THREE.Color('#2A7E98'), WET = new THREE.Color('#D2B97F'), DRY = new THREE.Color('#E8D29C');
const FOAM = new THREE.Color('#F2F5EC');
const STEP = 0.5, MARGIN = 2, SEABED_CELL = 4, SEABED_MARGIN = 72;

/** Colour by height: shelf → seabed with depth below the sea, wet → dry sand over 0.22 above it, and a foam band at the waterline. */
export function sandColour(y: number, out: THREE.Color): THREE.Color {
  if (y < SEA_LEVEL) out.copy(SHELF).lerp(SEABED, Math.min(1, (SEA_LEVEL - y) / 1.0));
  else out.copy(WET).lerp(DRY, Math.min(1, Math.max(0, (y - SEA_LEVEL - 0.04) / 0.22)));
  return out.lerp(FOAM, Math.max(0, 1 - Math.abs(y - SEA_LEVEL - 0.01) / 0.045) * 0.85);
}

export function buildBeach(map: Pick<MapData, 'w' | 'h' | 'tiles'>): THREE.Mesh {
  const height = beachHeight(map);
  const nx = Math.round((map.w + 2 * MARGIN) / STEP) + 1, nz = Math.round((map.h + 2 * MARGIN) / STEP) + 1;
  const pos = new Float32Array(nx * nz * 3), col = new Float32Array(nx * nz * 3);
  const c = new THREE.Color();
  for (let j = 0; j < nz; j++) for (let i = 0; i < nx; i++) {
    const x = -MARGIN + i * STEP, z = -MARGIN + j * STEP, y = height(x, z), k = j * nx + i;
    pos.set([x, y, z], k * 3);
    sandColour(y, c);
    col.set([c.r, c.g, c.b], k * 3);
  }
  // A quad is dropped when its tile and the eight around it are all main-level land (or water cut into it): the terraces cover it.
  const covered = (tx: number, ty: number) => {
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) if (!aboveBeach(map, tx + dx, ty + dy)) return false;
    return true;
  };
  const idx: number[] = [];
  for (let j = 0; j < nz - 1; j++) for (let i = 0; i < nx - 1; i++) {
    const tx = Math.floor(-MARGIN + (i + 0.5) * STEP), ty = Math.floor(-MARGIN + (j + 0.5) * STEP);
    if (covered(tx, ty)) continue;
    const a = j * nx + i;
    idx.push(a, a + nx, a + 1, a + 1, a + nx, a + nx + 1);
  }
  // One flat seabed beyond the map, so the open sea reads the same depth out to the fog: SEABED_CELL squares (small enough for the
  // world shader's per-vertex bend) around the fine grid, skipping the cells it already covers.
  const base = nx * nz, C = SEABED_CELL, deep = -1.6;
  const sx0 = -MARGIN - Math.ceil(SEABED_MARGIN / C) * C, sz0 = sx0;   // on the fine grid's lines, so its cells are wholly in or out
  const span = (n: number) => Math.ceil((n - 2 * sx0) / C) + 1, cnx = span(map.w), cnz = span(map.h);
  const pos2 = new Float32Array(pos.length + cnx * cnz * 3), col2 = new Float32Array(col.length + cnx * cnz * 3);
  pos2.set(pos); col2.set(col);
  for (let j = 0; j < cnz; j++) for (let i = 0; i < cnx; i++) {
    pos2.set([sx0 + i * C, deep, sz0 + j * C], (base + j * cnx + i) * 3);
    col2.set([SEABED.r, SEABED.g, SEABED.b], (base + j * cnx + i) * 3);
  }
  for (let j = 0; j < cnz - 1; j++) for (let i = 0; i < cnx - 1; i++) {
    const x = sx0 + i * C, z = sz0 + j * C;
    if (x >= -MARGIN && z >= -MARGIN && x + C <= map.w + MARGIN && z + C <= map.h + MARGIN) continue;
    const a = base + j * cnx + i;
    idx.push(a, a + cnx, a + 1, a + 1, a + cnx, a + cnx + 1);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos2, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(col2, 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array((pos2.length / 3) * 2), 2));
  geo.setIndex(idx);
  geo.computeVertexNormals();
  const n = geo.attributes.normal!, v = new THREE.Vector3(), up = new THREE.Vector3(0, 1, 0);
  for (let k = 0; k < n.count; k++) { v.fromBufferAttribute(n, k).lerp(up, 0.65).normalize(); n.setXYZ(k, v.x, v.y, v.z); }   // soft slope
  const sand = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95, metalness: 0 });
  sand.userData.ground = 'bare';                                   // shade, sun shadows and lamp pools on the sand too, no meadow patches
  const mesh = new THREE.Mesh(geo, sand);
  mesh.name = 'beach';
  mesh.receiveShadow = true;
  return mesh;
}
